import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../Docs.css';

export default function ReconnectHandling() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs/patterns/reconnects" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Reconnect Handling: Graceful Recovery Without Flapping</h1>
      </header>

      <main className="docs-lite__content">
        <p>Network disconnects are normal.</p>

        <p>
          Browsers sleep, mobile radios pause, Wi-Fi handoffs occur, and connections drop without
          warning.
          <br />
          A correct presence system treats reconnects as <strong>expected behavior</strong>, not
          failure.
        </p>

        <p>
          This document explains how to handle reconnects gracefully without causing user-level
          flapping or false offline states.
        </p>

        <hr />

        <section>
          <h2>Why reconnects are not failures</h2>
          <p>In real-world environments:</p>
          <ul>
            <li>laptops change networks</li>
            <li>mobile devices suspend radios</li>
            <li>background tabs pause timers</li>
            <li>brief packet loss interrupts connections</li>
          </ul>
          <p>
            A disconnect event does not mean a user has left.
            <br />
            A reconnect event does not mean a new session has begun.
          </p>
          <p>
            Presence systems must model reconnects as <strong>continuations</strong>, not exits and
            re-entries.
          </p>
        </section>

        <hr />

        <section>
          <h2>Disconnect does not equal offline</h2>
          <p>A common mistake is treating any disconnect as an immediate offline signal.</p>
          <p>This causes:</p>
          <ul>
            <li>false offline transitions</li>
            <li>rapid online/offline flapping</li>
            <li>unnecessary notifications</li>
            <li>loss of trust in presence indicators</li>
          </ul>
          <p>
            Offline should be a <strong>derived state</strong>, reached only after a grace period
            expires.
          </p>
        </section>

        <hr />

        <section>
          <h2>Grace windows and tolerance</h2>
          <p>Reconnect handling requires <strong>grace windows</strong>.</p>
          <p>
            A grace window is a short period during which a session is allowed to reconnect without
            changing its derived presence state.
          </p>
          <p>During the grace window:</p>
          <ul>
            <li>presence remains unchanged</li>
            <li>the session is marked as potentially reconnecting</li>
            <li>no offline events are emitted</li>
          </ul>
          <p>
            If the session fails to reconnect before the window expires, presence transitions
            normally.
          </p>
        </section>

        <hr />

        <section>
          <h2>Session identity and continuity</h2>
          <p>Reconnects should preserve <strong>session identity</strong> whenever possible.</p>
          <p>A stable session identifier allows:</p>
          <ul>
            <li>reconnects to resume the same session</li>
            <li>lifecycle state to continue smoothly</li>
            <li>idle and activity timers to remain meaningful</li>
          </ul>
          <p>
            Relying solely on transport-level identifiers (such as socket IDs) breaks continuity.
          </p>
          <p>Session identity must be owned by the application, not the transport.</p>
        </section>

        <hr />

        <section>
          <h2>Reconnect versus new session</h2>
          <p>Not every reconnect should be treated as a continuation.</p>
          <p>A reconnect may be treated as a new session when:</p>
          <ul>
            <li>the grace window has expired</li>
            <li>the client explicitly resets state</li>
            <li>the reconnect occurs after a long absence</li>
          </ul>
          <p>The distinction is temporal, not event-based.</p>
          <p>Time determines continuity.</p>
        </section>

        <hr />

        <section>
          <h2>Avoiding flapping during reconnect storms</h2>
          <p>Rapid connect/disconnect cycles can occur during:</p>
          <ul>
            <li>unstable networks</li>
            <li>VPN changes</li>
            <li>device sleep/wake transitions</li>
          </ul>
          <p>To prevent flapping:</p>
          <ul>
            <li>suppress state transitions during the grace window</li>
            <li>debounce reconnect events</li>
            <li>recompute presence from timestamps, not events</li>
          </ul>
          <p>Presence should reflect <strong>confidence</strong>, not instantaneous events.</p>
        </section>

        <hr />

        <section>
          <h2>Interaction with idle detection</h2>
          <p>Reconnect handling must integrate with idle detection.</p>
          <p>A reconnect does not automatically imply activity.</p>
          <p>After reconnect:</p>
          <ul>
            <li>restore prior lifecycle state</li>
            <li>wait for activity signals</li>
            <li>avoid forcing active state prematurely</li>
          </ul>
          <p>This preserves presence accuracy across reconnects.</p>
        </section>

        <hr />

        <section>
          <h2>Common failure modes</h2>
          <p>Reconnect handling fails when systems:</p>
          <ul>
            <li>mark offline on every disconnect</li>
            <li>treat every reconnect as a new user</li>
            <li>reset idle timers on reconnect</li>
            <li>emit excessive reconnect events</li>
            <li>ignore session identity</li>
          </ul>
          <p>These failures stem from <strong>event-first modeling</strong>.</p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>Reconnects are normal.</p>
          <p>A correct reconnect handling pattern:</p>
          <ul>
            <li>uses grace windows</li>
            <li>preserves session identity</li>
            <li>derives presence from time, not events</li>
            <li>suppresses flapping</li>
            <li>integrates with idle detection</li>
          </ul>
          <p>
            When reconnects are handled correctly, presence remains stable under real-world network
            conditions.
          </p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
            <li>
              <Link to="/docs/patterns/idle-detection.md">Idle Detection</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/patterns/reconnects.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
